import React, { useState, useEffect } from 'react';
import { assetQuizAPI } from '../services/api';
import ReportIssueButton from './ReportIssueButton';

function AssetQuizInterface({ asset, isDarkMode = false, onQuizDataChange }) {
  const [assetQuiz, setAssetQuiz] = useState(null);
  const [quizSubmissions, setQuizSubmissions] = useState([]);
  const [attemptsRemaining, setAttemptsRemaining] = useState(0);
  const [takingQuiz, setTakingQuiz] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState([]);
  const [currentQuizQuestion, setCurrentQuizQuestion] = useState(0);
  const [quizStartTime, setQuizStartTime] = useState(null);
  const [quizSubmission, setQuizSubmission] = useState(null);
  const [viewingSubmission, setViewingSubmission] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAnswerForCurrent, setShowAnswerForCurrent] = useState(false);

  // Helper functions for localStorage persistence
  const getStorageKey = (quizId) => `assetQuiz_${quizId}_progress`;

  const saveQuizProgress = (quizId, answers, currentQuestion, startTime) => {
    try {
      const progress = {
        answers,
        currentQuestion,
        startTime,
        savedAt: Date.now()
      };
      localStorage.setItem(getStorageKey(quizId), JSON.stringify(progress));
    } catch (error) {
      console.error('Error saving quiz progress:', error);
    }
  };

  const loadQuizProgress = (quizId) => {
    try {
      const saved = localStorage.getItem(getStorageKey(quizId));
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.error('Error loading quiz progress:', error);
    }
    return null;
  };

  const clearQuizProgress = (quizId) => {
    try {
      localStorage.removeItem(getStorageKey(quizId));
    } catch (error) {
      console.error('Error clearing quiz progress:', error);
    }
  };

  useEffect(() => {
    // Reset all quiz UI state when asset changes
    setTakingQuiz(false);
    setQuizAnswers([]);
    setCurrentQuizQuestion(0);
    setQuizStartTime(null);
    setQuizSubmission(null);
    setViewingSubmission(null);
    setShowAnswerForCurrent(false);
    
    // Clear any saved progress for previous asset
    if (assetQuiz?._id) {
      clearQuizProgress(assetQuiz._id);
    }
    
    if (asset && (asset.assetType === 'note' || asset.assetType === 'markdown' || asset.assetType === 'transcript') && asset.content) {
      fetchAssetQuiz();
    } else {
      // If not a note/markdown asset, clear quiz data
      setAssetQuiz(null);
      setQuizSubmissions([]);
      setAttemptsRemaining(0);
      if (onQuizDataChange) {
        onQuizDataChange({
          hasQuiz: false,
          hasAttempts: false
        });
      }
    }
  }, [asset?._id]);

  const fetchAssetQuiz = async () => {
    try {
      setLoading(true);
      const response = await assetQuizAPI.getAssetQuiz(asset._id);
      setAssetQuiz(response.data.assetQuiz);
      setQuizSubmissions(response.data.submissions || []);
      setAttemptsRemaining(response.data.attemptsRemaining);
      
      // Notify parent about quiz data
      if (onQuizDataChange) {
        onQuizDataChange({
          hasQuiz: !!response.data.assetQuiz,
          hasAttempts: (response.data.submissions || []).length > 0
        });
      }
    } catch (err) {
      // No quiz exists for this asset, which is fine
      setAssetQuiz(null);
      setQuizSubmissions([]);
      setAttemptsRemaining(0);
      
      // Notify parent that there's no quiz
      if (onQuizDataChange) {
        onQuizDataChange({
          hasQuiz: false,
          hasAttempts: false
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const startQuiz = () => {
    // Check for saved progress
    const savedProgress = loadQuizProgress(assetQuiz._id);
    
    if (savedProgress) {
      // Ask user if they want to continue where they left off
      const continueFromSaved = window.confirm(
        'We found your previous quiz progress. Would you like to continue where you left off?\n\n' +
        `Answered: ${savedProgress.answers.filter(a => a).length}/${assetQuiz.questions.length} questions\n\n` +
        'Click "OK" to continue, or "Cancel" to start fresh.'
      );
      
      if (continueFromSaved) {
        setTakingQuiz(true);
        setQuizAnswers(savedProgress.answers);
        setCurrentQuizQuestion(savedProgress.currentQuestion);
        setQuizStartTime(savedProgress.startTime);
        setQuizSubmission(null);
        setShowAnswerForCurrent(false);
        return;
      } else {
        // User wants to start fresh, clear saved progress
        clearQuizProgress(assetQuiz._id);
      }
    }
    
    // Start fresh quiz
    setTakingQuiz(true);
    setQuizAnswers([]);
    setCurrentQuizQuestion(0);
    setQuizStartTime(Date.now());
    setQuizSubmission(null);
    setShowAnswerForCurrent(false);
  };

  const handleQuizAnswerSelect = (selectedAnswer) => {
    const newAnswers = [...quizAnswers];
    newAnswers[currentQuizQuestion] = {
      questionIndex: currentQuizQuestion,
      selectedAnswer: selectedAnswer
    };
    setQuizAnswers(newAnswers);
    
    // Save progress to localStorage
    saveQuizProgress(assetQuiz._id, newAnswers, currentQuizQuestion, quizStartTime);
    
    // Show answer immediately if the setting is enabled
    if (assetQuiz.settings?.showAnswerAfterEach) {
      setShowAnswerForCurrent(true);
    }
  };

  const nextQuizQuestion = () => {
    if (currentQuizQuestion < assetQuiz.questions.length - 1) {
      const newQuestionIndex = currentQuizQuestion + 1;
      setCurrentQuizQuestion(newQuestionIndex);
      // Save progress with new question index
      saveQuizProgress(assetQuiz._id, quizAnswers, newQuestionIndex, quizStartTime);
      // Show answer if user already answered this question and showAnswerAfterEach is enabled
      const nextQuestionAnswered = quizAnswers[newQuestionIndex] !== undefined;
      setShowAnswerForCurrent(assetQuiz.settings?.showAnswerAfterEach && nextQuestionAnswered);
    }
  };

  const previousQuizQuestion = () => {
    if (currentQuizQuestion > 0) {
      const newQuestionIndex = currentQuizQuestion - 1;
      setCurrentQuizQuestion(newQuestionIndex);
      // Save progress with new question index
      saveQuizProgress(assetQuiz._id, quizAnswers, newQuestionIndex, quizStartTime);
      // Show answer if user already answered this question and showAnswerAfterEach is enabled
      const prevQuestionAnswered = quizAnswers[newQuestionIndex] !== undefined;
      setShowAnswerForCurrent(assetQuiz.settings?.showAnswerAfterEach && prevQuestionAnswered);
    }
  };

  const submitAssetQuiz = async () => {
    // Validate all questions answered
    if (quizAnswers.length !== assetQuiz.questions.length) {
      alert('Please answer all questions before submitting');
      return;
    }

    if (!window.confirm('Are you sure you want to submit your quiz? You cannot change your answers after submitting.')) {
      return;
    }

    try {
      const timeSpent = Math.floor((Date.now() - quizStartTime) / 1000);
      const response = await assetQuizAPI.submitQuiz({
        assetQuizId: assetQuiz._id,
        answers: quizAnswers,
        timeSpent
      });

      setQuizSubmission(response.data.submission);
      setAttemptsRemaining(response.data.attemptsRemaining);
      setTakingQuiz(false);
      
      // Clear saved progress after successful submission
      clearQuizProgress(assetQuiz._id);
      
      // Refresh submissions list
      fetchAssetQuiz();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to submit quiz');
      console.error('Error submitting quiz:', err);
    }
  };

  const viewSubmissionDetails = async (submission) => {
    try {
      const response = await assetQuizAPI.getSubmission(submission._id);
      setViewingSubmission(response.data);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to load submission details');
      console.error('Error fetching submission:', err);
    }
  };

  if (loading || !assetQuiz) {
    return null;
  }

  // Quiz List View
  if (!takingQuiz && !quizSubmission && !viewingSubmission) {
    return (
      <div style={{
        marginTop: '32px',
        padding: '24px',
        background: isDarkMode ? '#2d3748' : '#ffffff',
        borderRadius: '12px',
        border: `2px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <span style={{ fontSize: '28px' }}>📝</span>
          <div>
            <h3 style={{ margin: 0, fontSize: '20px', color: isDarkMode ? '#f3f4f6' : '#1a202c' }}>
              Quiz Available
            </h3>
            <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: isDarkMode ? '#d1d5db' : '#718096' }}>
              Test your understanding with {assetQuiz.questions.length} questions
            </p>
          </div>
        </div>
        
        {quizSubmissions.length > 0 && (
          <div style={{ marginBottom: '16px', padding: '12px', background: isDarkMode ? '#374151' : '#f7fafc', borderRadius: '8px' }}>
            <strong style={{ color: isDarkMode ? '#f3f4f6' : '#1a202c', fontSize: '14px' }}>
              Your Attempts ({quizSubmissions.length}/3)
            </strong>
            <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {quizSubmissions.map((sub, idx) => (
                <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px', background: isDarkMode ? '#2d3748' : '#ffffff', borderRadius: '6px' }}>
                  <span style={{ fontSize: '13px', color: isDarkMode ? '#d1d5db' : '#4a5568' }}>
                    Attempt {sub.attemptNumber}: {sub.score}% ({sub.correctAnswers}/{sub.totalQuestions})
                  </span>
                  <button
                    onClick={() => viewSubmissionDetails(sub)}
                    style={{ fontSize: '12px', color: '#667eea', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    Review
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {attemptsRemaining > 0 ? (
          <button
            onClick={startQuiz}
            style={{
              padding: '12px 24px',
              background: '#667eea',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '15px',
              fontWeight: '600',
              width: '100%'
            }}
          >
            {quizSubmissions.length > 0 ? `Retake Quiz (${attemptsRemaining} attempts left)` : 'Start Quiz'}
          </button>
        ) : (
          <p style={{ margin: 0, color: '#e53e3e', fontSize: '14px', fontWeight: '600' }}>
            You have used all 3 attempts for this quiz.
          </p>
        )}
      </div>
    );
  }

  // Taking Quiz Interface
  if (takingQuiz && assetQuiz) {
    return (
      <div style={{
        marginTop: '32px',
        padding: '24px',
        background: isDarkMode ? '#2d3748' : '#ffffff',
        borderRadius: '12px',
        border: `2px solid ${isDarkMode ? '#4a5568' : '#667eea'}`
      }}>
        <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h3 style={{ margin: 0, fontSize: '18px', color: isDarkMode ? '#f3f4f6' : '#1a202c' }}>
              Question {currentQuizQuestion + 1} of {assetQuiz.questions.length}
            </h3>
            <ReportIssueButton
              sourceType="assetQuiz"
              sourceId={assetQuiz._id}
              questionPath={`questions.${currentQuizQuestion}`}
              questionId={assetQuiz.questions[currentQuizQuestion]._id}
              questionIndex={currentQuizQuestion}
              questionData={{
                question: assetQuiz.questions[currentQuizQuestion].question,
                options: assetQuiz.questions[currentQuizQuestion].options,
                correctAnswer: assetQuiz.questions[currentQuizQuestion].correctAnswer,
                questionType: assetQuiz.questions[currentQuizQuestion].type,
                difficulty: assetQuiz.questions[currentQuizQuestion].difficulty,
                explanation: assetQuiz.questions[currentQuizQuestion].explanation
              }}
              metadata={{
                sourceName: assetQuiz.title || `${asset.title} Quiz`,
                assetTitle: asset.title,
                assetType: asset.assetType,
                questionNumber: currentQuizQuestion + 1,
                totalQuestions: assetQuiz.questions?.length || 0,
                userAnswer: quizAnswers[currentQuizQuestion]?.selectedAnswer,
                isCorrect: quizAnswers[currentQuizQuestion]?.isCorrect,
                reportedFrom: 'asset-quiz-taking',
                timestamp: new Date().toISOString()
              }}
              size="icon-only"
            />
          </div>
          <span style={{ fontSize: '13px', color: isDarkMode ? '#a0aec0' : '#718096' }}>
            {quizAnswers.filter(a => a !== undefined).length} / {assetQuiz.questions.length} answered
          </span>
        </div>
        
        {assetQuiz.questions[currentQuizQuestion] && (
          <div>
            <div style={{ marginBottom: '8px', padding: '8px 12px', background: isDarkMode ? '#374151' : '#f0f9ff', borderRadius: '6px', display: 'inline-block' }}>
              <span style={{ fontSize: '12px', fontWeight: '600', color: isDarkMode ? '#a0aec0' : '#0369a1' }}>
                {assetQuiz.questions[currentQuizQuestion].difficulty}
              </span>
            </div>
            
            <p style={{ fontSize: '16px', fontWeight: '600', marginBottom: '20px', color: isDarkMode ? '#e2e8f0' : '#2d3748' }}>
              {assetQuiz.questions[currentQuizQuestion].question}
            </p>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
              {assetQuiz.questions[currentQuizQuestion].options.map((option, idx) => {
                const isSelected = quizAnswers[currentQuizQuestion]?.selectedAnswer === idx;
                const isCorrect = idx === assetQuiz.questions[currentQuizQuestion].correctAnswer;
                const showFeedback = assetQuiz.settings?.showAnswerAfterEach && showAnswerForCurrent;
                
                return (
                  <label
                    key={idx}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '14px',
                      background: showFeedback 
                        ? (isCorrect ? (isDarkMode ? '#1a3a32' : '#d1fae5') : (isSelected ? (isDarkMode ? '#3a2a2a' : '#fee2e2') : (isDarkMode ? '#374151' : '#f7fafc')))
                        : (isSelected ? (isDarkMode ? '#4a5568' : '#e6f2ff') : (isDarkMode ? '#374151' : '#f7fafc')),
                      border: showFeedback
                        ? `2px solid ${isCorrect ? '#48bb78' : (isSelected ? '#ef4444' : (isDarkMode ? '#4a5568' : '#e2e8f0'))}`
                        : `2px solid ${isSelected ? '#667eea' : (isDarkMode ? '#4a5568' : '#e2e8f0')}`,
                      borderRadius: '8px',
                      cursor: showFeedback ? 'default' : 'pointer',
                      transition: 'all 0.2s',
                      position: 'relative'
                    }}
                  >
                    <input
                      type="radio"
                      name={`question-${currentQuizQuestion}`}
                      checked={isSelected}
                      onChange={() => !showFeedback && handleQuizAnswerSelect(idx)}
                      disabled={showFeedback}
                      style={{ width: '20px', height: '20px', cursor: showFeedback ? 'default' : 'pointer' }}
                    />
                    <span style={{ 
                      fontSize: '15px', 
                      color: showFeedback 
                        ? (isCorrect ? '#065f46' : (isSelected ? '#991b1b' : (isDarkMode ? '#d1d5db' : '#4a5568')))
                        : (isDarkMode ? '#e2e8f0' : '#2d3748'),
                      flex: 1
                    }}>
                      {option}
                    </span>
                    {showFeedback && isCorrect && (
                      <span style={{ fontSize: '18px' }}>✓</span>
                    )}
                    {showFeedback && isSelected && !isCorrect && (
                      <span style={{ fontSize: '18px' }}>✗</span>
                    )}
                  </label>
                );
              })}
            </div>
            
            {/* Show explanation after answer is selected (if showAnswerAfterEach is enabled) */}
            {assetQuiz.settings?.showAnswerAfterEach && showAnswerForCurrent && assetQuiz.questions[currentQuizQuestion].explanation && (
              <div style={{
                marginBottom: '20px',
                padding: '16px',
                background: isDarkMode ? '#374151' : '#fef3c7',
                borderLeft: `4px solid #f59e0b`,
                borderRadius: '8px'
              }}>
                <div style={{ fontWeight: '600', fontSize: '14px', color: isDarkMode ? '#fbbf24' : '#92400e', marginBottom: '8px' }}>
                  💡 Explanation
                </div>
                <p style={{ margin: 0, fontSize: '14px', color: isDarkMode ? '#d1d5db' : '#78350f', lineHeight: '1.6' }}>
                  {assetQuiz.questions[currentQuizQuestion].explanation}
                </p>
              </div>
            )}
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'space-between' }}>
              <button
                onClick={previousQuizQuestion}
                disabled={currentQuizQuestion === 0}
                style={{
                  padding: '10px 20px',
                  background: currentQuizQuestion === 0 ? '#cbd5e1' : (isDarkMode ? '#4a5568' : '#e2e8f0'),
                  color: currentQuizQuestion === 0 ? '#64748b' : (isDarkMode ? '#e2e8f0' : '#2d3748'),
                  border: 'none',
                  borderRadius: '8px',
                  cursor: currentQuizQuestion === 0 ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                ← Previous
              </button>
              
              <div style={{ display: 'flex', gap: '12px' }}>
                {currentQuizQuestion < assetQuiz.questions.length - 1 ? (
                  <button
                    onClick={nextQuizQuestion}
                    style={{
                      padding: '10px 20px',
                      background: '#667eea',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                  >
                    Next →
                  </button>
                ) : (
                  <button
                    onClick={submitAssetQuiz}
                    disabled={quizAnswers.length !== assetQuiz.questions.length}
                    style={{
                      padding: '10px 20px',
                      background: quizAnswers.length === assetQuiz.questions.length ? '#48bb78' : '#cbd5e1',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: quizAnswers.length === assetQuiz.questions.length ? 'pointer' : 'not-allowed',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                  >
                    Submit Quiz
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Quiz Results
  if (quizSubmission) {
    return (
      <div style={{
        marginTop: '32px',
        padding: '24px',
        background: isDarkMode ? '#2d3748' : '#ffffff',
        borderRadius: '12px',
        border: `2px solid ${quizSubmission.score >= 70 ? '#48bb78' : '#f59e0b'}`
      }}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>
            {quizSubmission.score >= 70 ? '🎉' : '📚'}
          </div>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '24px', color: quizSubmission.score >= 70 ? '#48bb78' : '#f59e0b' }}>
            Score: {quizSubmission.score}%
          </h3>
          <p style={{ margin: 0, fontSize: '16px', color: isDarkMode ? '#d1d5db' : '#4a5568' }}>
            {quizSubmission.correctAnswers} out of {quizSubmission.totalQuestions} correct
          </p>
          {quizSubmission.score >= 70 ? (
            <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#48bb78', fontWeight: '600' }}>
              Great job! You passed the quiz.
            </p>
          ) : (
            <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#f59e0b', fontWeight: '600' }}>
              Keep studying and try again!
            </p>
          )}
        </div>
        
        <button
          onClick={() => {
            setQuizSubmission(null);
            fetchAssetQuiz();
          }}
          style={{
            padding: '12px 24px',
            background: '#667eea',
            color: '#ffffff',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '15px',
            fontWeight: '600',
            width: '100%'
          }}
        >
          Back to Quiz
        </button>
      </div>
    );
  }

  // View Submission Details
  if (viewingSubmission) {
    return (
      <div style={{
        marginTop: '32px',
        padding: '24px',
        background: isDarkMode ? '#2d3748' : '#ffffff',
        borderRadius: '12px',
        border: `2px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`
      }}>
        <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ margin: '0 0 4px 0', fontSize: '20px', color: isDarkMode ? '#f3f4f6' : '#1a202c' }}>
              Quiz Review - Attempt {viewingSubmission.submission.attemptNumber}
            </h3>
            <p style={{ margin: 0, fontSize: '16px', color: viewingSubmission.submission.score >= 70 ? '#48bb78' : '#f59e0b' }}>
              Score: {viewingSubmission.submission.score}% ({viewingSubmission.submission.correctAnswers}/{viewingSubmission.submission.totalQuestions})
            </p>
          </div>
          <button
            onClick={() => setViewingSubmission(null)}
            style={{ padding: '8px 16px', background: isDarkMode ? '#4a5568' : '#e2e8f0', color: isDarkMode ? '#e2e8f0' : '#2d3748', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
          >
            Close
          </button>
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {viewingSubmission.questions.map((q, idx) => {
            const answer = viewingSubmission.submission.answers.find(a => a.questionIndex === idx);
            return (
              <div key={idx} style={{
                padding: '16px',
                background: answer?.isCorrect ? (isDarkMode ? '#1a3a32' : '#f0fdf4') : (isDarkMode ? '#3a2a2a' : '#fef2f2'),
                borderRadius: '8px',
                border: `2px solid ${answer?.isCorrect ? '#48bb78' : '#ef4444'}`
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <div style={{ padding: '4px 8px', background: isDarkMode ? '#374151' : 'rgba(255,255,255,0.5)', borderRadius: '4px' }}>
                    <span style={{ fontSize: '11px', fontWeight: '600', color: isDarkMode ? '#a0aec0' : '#64748b' }}>
                      {q.difficulty}
                    </span>
                  </div>
                  <ReportIssueButton
                    sourceType="assetQuiz"
                    sourceId={assetQuiz._id}
                    questionPath={`questions.${idx}`}
                    questionId={q._id}
                    questionIndex={idx}
                    questionData={{
                      question: q.question,
                      options: q.options,
                      correctAnswer: q.correctAnswer,
                      questionType: q.type,
                      difficulty: q.difficulty,
                      explanation: q.explanation
                    }}
                    metadata={{
                      sourceName: assetQuiz.title || `${asset.title} Quiz`,
                      assetTitle: asset.title,
                      assetType: asset.assetType,
                      questionNumber: idx + 1,
                      totalQuestions: assetQuiz.questions?.length || 0,
                      userAnswer: answer?.selectedAnswer,
                      isCorrect: answer?.isCorrect,
                      submissionId: viewingSubmission.submission._id,
                      attemptNumber: viewingSubmission.submission.attemptNumber,
                      reportedFrom: 'asset-quiz-review',
                      timestamp: new Date().toISOString()
                    }}
                    size="icon-only"
                  />
                </div>
                <p style={{ fontWeight: '600', marginBottom: '12px', color: isDarkMode ? '#f3f4f6' : '#1a202c' }}>
                  {idx + 1}. {q.question}
                </p>
                <div style={{ fontSize: '14px', color: isDarkMode ? '#d1d5db' : '#4a5568' }}>
                  <p style={{ margin: '4px 0' }}>
                    <strong>Your answer:</strong> {q.options[answer?.selectedAnswer]}
                    {answer?.isCorrect ? ' ✓' : ' ✗'}
                  </p>
                  {!answer?.isCorrect && (
                    <p style={{ margin: '4px 0', color: '#48bb78' }}>
                      <strong>Correct answer:</strong> {q.options[q.correctAnswer]}
                    </p>
                  )}
                  {q.explanation && (
                    <p style={{ margin: '8px 0 0 0', fontStyle: 'italic', fontSize: '13px', padding: '8px', background: isDarkMode ? '#374151' : 'rgba(255,255,255,0.5)', borderRadius: '4px' }}>
                      💡 {q.explanation}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return null;
}

export default AssetQuizInterface;
